/**
 * Client for maintaining product series (/administration/product-series/* on tri-server, platform root only;
 * tri-server docs/product-series-entwurf.md §9a). Mirrors SynonymsAPI: authenticatedFetch + processResponse.
 */
import { configuration } from "./Configuration";
import { ApplicationError, UnauthorizedError } from "./general/Errors";
import { authenticatedFetch } from "./JWTAuthService";

export interface ProductSeriesRow {
  _id: string;
  name: string;
  aliases: string[];
  evidence?: "datasheetSplit" | "crossDocument" | "mention" | "manual";
  status?: "derived" | "confirmed";
  source?: string;
  din276Category?: string;
  manufacturer: { _id: string | null; name: string };
  products: number;
  documents: number;
}

export interface ProductSeriesDetail {
  series: any;
  products: Array<{ _id: string; name?: string; model?: string; documents: number }>;
  documents: Array<{ _id: string; name: string; roles: string[] }>;
  assets: number;
}

const processResponse = async (response: Response) => {
  if (response.ok) return response.json();
  if (response.status === 401) throw new UnauthorizedError();
  let errorMessage = `${response.status} ${response.statusText}`;
  try {
    const errorData = await response.json();
    errorMessage = errorData.message || errorData.error || errorMessage;
  } catch {
    // body not JSON
  }
  throw new ApplicationError(errorMessage);
};

/** Query string from the set parameters only. */
export const queryString = (params: Record<string, unknown> = {}): string => {
  const entries = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => [k, String(v)]);
  return entries.length ? `?${new URLSearchParams(entries).toString()}` : "";
};

export const useProductSeriesAPI = () => {
  const baseUrl = `${configuration.serverUrl}/administration/product-series`;
  const send = async (method: string, path: string, body?: unknown) =>
    processResponse(
      await authenticatedFetch(`${baseUrl}${path}`, {
        method,
        headers: { "Content-Type": "application/json" },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      }),
    );

  return {
    list: (params: { q?: string; status?: string; manufacturer?: string; skip?: number; limit?: number }) =>
      send("GET", queryString(params)) as Promise<{ total: number; items: ProductSeriesRow[] }>,
    get: (id: string) => send("GET", `/${id}`) as Promise<ProductSeriesDetail>,
    confirm: (id: string) => send("POST", `/${id}/confirm`),
    update: (id: string, edit: { name?: string; aliases?: string[]; din276Category?: string }) => send("PUT", `/${id}`, edit),
    merge: (id: string, into: string) => send("POST", `/${id}/merge`, { into }),
    split: (id: string, parts?: string[]) => send("POST", `/${id}/split`, parts ? { parts } : {}),
  };
};
